import { i18n } from '@quadralize/i18n'
import { Filter, Magnifier } from '@solar-icons/react'
import { useTranslation } from 'react-i18next'
import { Input } from '../../forms/input/input'
import { Button } from '../../ui/button/button'
import { Badge } from '../badge/badge'
import { DataTableFilterDrawer } from './data-table-filter-drawer'
import {
  type DataTableFilterDrawerProps,
  type DataTableSearchConfig,
  getActiveFilterCount,
} from './data-table-filters.types'

interface DataTableMobileFiltersProps {
  /** Search configuration */
  search?: DataTableSearchConfig
  /** Filter configurations (enables the filter drawer trigger) */
  filters?: DataTableFilterDrawerProps['filters']
  /** Callback to clear all filters */
  onClearFilters?: () => void
  /** Filter drawer title */
  filterDrawerTitle?: string
  /** Filter drawer description */
  filterDrawerDescription?: string
}

/**
 * Mobile toolbar: search input + inline filter button that opens a drawer.
 * Renders as a row so the filter trigger is always visible next to search.
 */
export function DataTableMobileFilters({
  search,
  filters,
  onClearFilters,
  filterDrawerTitle,
  filterDrawerDescription,
}: DataTableMobileFiltersProps) {
  const { t } = useTranslation('ui', { i18n })
  if (!search && !filters) {
    return null
  }

  const hasFilterDrawer = filters && filters.length > 0 && onClearFilters
  const activeFilterCount = filters ? getActiveFilterCount(filters) : 0
  const hasActiveFilters = activeFilterCount > 0

  return (
    <div className="flex items-center gap-2 mb-4">
      {search && (
        <div className="relative flex-1 min-w-0">
          <Input
            startIcon={<Magnifier />}
            placeholder={search.placeholder}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            clearable
            clearLabel={t('dataTable.filters.clearSearch')}
            className="pl-9 h-10"
          />
        </div>
      )}

      {hasFilterDrawer && (
        <DataTableFilterDrawer
          filters={filters}
          onClearFilters={onClearFilters}
          title={filterDrawerTitle}
          description={filterDrawerDescription}
          hasSearch={!!search?.value}
          trigger={
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 overflow-visible [&>div]:overflow-visible"
              aria-label={t('dataTable.filters.openFilters')}
            >
              <div className="relative">
                <Filter className="h-4 w-4" />
                {hasActiveFilters && (
                  <Badge
                    variant="default"
                    className="absolute -right-3.5 -top-3.5 h-5 min-w-5 rounded-full px-1 text-[10px] leading-none flex items-center justify-center border-2 border-bg-base z-10"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </div>
            </Button>
          }
        />
      )}
    </div>
  )
}
